import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState, type FormEvent } from "react"
import { api, toApiMessage } from "../api/client"
import type { AuditEvent, InstanceOverview, Paged } from "../api/types"
import { useAuth } from "../auth/AuthContext"
import { Icon } from "../components/Icon"
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  showToast
} from "../components/ui"
import { formatRelative } from "../lib/format"
import { auditActionLabel, auditTargetLabel } from "../lib/audit"
import { usePublicConfig } from "../config/PublicConfigContext"
import { copyText } from "../lib/clipboard"

interface SystemFamily {
  id: string
  name: string
  status: string
  membersCount?: number
  currenciesCount?: number
  createdAt: string
  _count?: { members?: number; currencies?: number }
}
interface SystemUser {
  id: string
  login: string
  displayName: string
  systemRole: string
  status?: string
  familiesCount?: number
  createdAt: string
  _count?: { memberships?: number; sessions?: number }
}

interface StatusAction {
  kind: "user" | "family"
  id: string
  name: string
  nextStatus: "ACTIVE" | "SUSPENDED"
}

function Pagination({ page, pages, onChange }: { page: number; pages: number; onChange(page: number): void }) {
  return (
    <div className="pagination-controls" aria-label={`Страница ${page} из ${pages}`}>
      <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        Назад
      </Button>
      <span>
        {page} из {pages}
      </span>
      <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => onChange(page + 1)}>
        Дальше
      </Button>
    </div>
  )
}

function StatusDialog({
  action,
  pending,
  error,
  onClose,
  onConfirm
}: {
  action: StatusAction | null
  pending: boolean
  error?: string
  onClose(): void
  onConfirm(reason: string): void
}) {
  const [reason, setReason] = useState("")
  if (!action) return null
  const suspending = action.nextStatus === "SUSPENDED"
  const submit = (event: FormEvent) => {
    event.preventDefault()
    onConfirm(reason)
  }
  const consequence =
    action.kind === "user"
      ? suspending
        ? "Вход будет закрыт сразу, активные сессии пользователя будут отозваны."
        : "Пользователь снова сможет войти в свои семьи."
      : suspending
        ? "Операции этой семьи будут приостановлены. Аккаунты участников и другие семьи останутся доступны."
        : "Семейные операции снова будут доступны."
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${suspending ? "Приостановить" : "Активировать"} «${action.name}»`}
      eyebrow="Системное действие"
    >
      <form className="form-stack" onSubmit={submit}>
        <div className={`info-callout ${suspending ? "info-callout--warning" : ""}`}>
          <Icon name="shield" size={18} />
          <p>{consequence}</p>
        </div>
        <Field label="Причина">
          <textarea
            required
            minLength={3}
            maxLength={500}
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            autoFocus
          />
        </Field>
        {error ? <div className="form-error">{error}</div> : null}
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" variant={suspending ? "danger" : "primary"} loading={pending}>
            {suspending ? "Приостановить" : "Активировать"}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function RegistrationInviteDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const queryClient = useQueryClient()
  const [expiresInHours, setExpiresInHours] = useState(168)
  const [result, setResult] = useState<{ token: string; invitation: { expiresAt: string } } | null>(null)
  const invitations = useQuery({
    queryKey: ["instance", "registration-invitations"],
    queryFn: () => api.get<Paged<{ id: string; expiresAt: string; status: string }>>("/admin/registration-invitations"),
    enabled: open
  })
  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ token: string; invitation: { expiresAt: string } }>("/admin/registration-invitations", {
        expiresInHours
      }),
    onSuccess: async (created) => {
      setResult(created)
      await queryClient.invalidateQueries({ queryKey: ["instance", "registration-invitations"] })
    }
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/registration-invitations/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["instance", "registration-invitations"] })
      showToast("Приглашение отозвано", "neutral")
    },
    onError: (error) => showToast(toApiMessage(error, "Не удалось отозвать приглашение"), "danger")
  })
  const link = result ? `${location.origin}/register/${encodeURIComponent(result.token)}` : ""
  const close = () => {
    setResult(null)
    onClose()
  }
  return (
    <Dialog open={open} onClose={close} title={result ? "Ссылка готова" : "Пригласить новую семью"}>
      {result ? (
        <div className="invite-result">
          <span className="invite-result__icon">🏡</span>
          <p>Ссылка одноразовая и действует до {new Date(result.invitation.expiresAt).toLocaleString("ru-RU")}.</p>
          <div className="copy-field">
            <input readOnly value={link} />
            <Button
              size="icon"
              aria-label="Скопировать ссылку"
              onClick={() =>
                void copyText(link).then((copied) =>
                  showToast(
                    copied ? "Ссылка скопирована" : "Выделите ссылку и скопируйте вручную",
                    copied ? "success" : "neutral"
                  )
                )
              }
            >
              <Icon name="copy" />
            </Button>
          </div>
          <div className="dialog__actions">
            <Button onClick={close}>Готово</Button>
          </div>
        </div>
      ) : (
        <form
          className="form-stack"
          onSubmit={(event) => {
            event.preventDefault()
            mutation.mutate()
          }}
        >
          <p className="muted">
            Получатель создаст собственный аккаунт и новую семью. Ссылка сработает только один раз.
          </p>
          <Field label="Срок новой ссылки">
            <select value={expiresInHours} onChange={(event) => setExpiresInHours(Number(event.target.value))}>
              <option value={24}>1 день</option>
              <option value={168}>7 дней</option>
              <option value={336}>14 дней</option>
            </select>
          </Field>
          <Button type="submit" loading={mutation.isPending}>
            Создать ссылку
          </Button>
          {mutation.isError ? <div className="form-error">{toApiMessage(mutation.error)}</div> : null}
          {invitations.isLoading ? (
            <LoadingState rows={2} />
          ) : invitations.isError ? (
            <ErrorState message={toApiMessage(invitations.error)} retry={() => void invitations.refetch()} />
          ) : invitations.data?.items.length ? (
            <div className="pending-registration-invites">
              <strong>Активные приглашения</strong>
              {invitations.data.items.map((invitation) => (
                <div key={invitation.id}>
                  <span>До {new Date(invitation.expiresAt).toLocaleString("ru-RU")}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={revoke.isPending}
                    onClick={() => revoke.mutate(invitation.id)}
                  >
                    Отозвать
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="dialog__actions">
            <Button type="button" variant="ghost" onClick={close}>
              Закрыть
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  )
}

export function InstanceAdminPage() {
  const { user } = useAuth()
  const { registrationEnabled } = usePublicConfig()
  const queryClient = useQueryClient()
  const [statusAction, setStatusAction] = useState<StatusAction | null>(null)
  const [registrationInviteOpen, setRegistrationInviteOpen] = useState(false)
  const [familyPage, setFamilyPage] = useState(1)
  const [userPage, setUserPage] = useState(1)
  const [auditPage, setAuditPage] = useState(1)
  const overview = useQuery({
    queryKey: ["instance", "overview"],
    queryFn: () => api.get<InstanceOverview>("/admin/overview"),
    enabled: user?.systemRole === "INSTANCE_ADMIN",
    refetchInterval: 30_000
  })
  const families = useQuery({
    queryKey: ["instance", "families", familyPage],
    queryFn: () => api.get<Paged<SystemFamily>>(`/admin/families?page=${familyPage}&pageSize=20`),
    enabled: user?.systemRole === "INSTANCE_ADMIN",
    placeholderData: (previous) => previous
  })
  const users = useQuery({
    queryKey: ["instance", "users", userPage],
    queryFn: () => api.get<Paged<SystemUser>>(`/admin/users?page=${userPage}&pageSize=20`),
    enabled: user?.systemRole === "INSTANCE_ADMIN",
    placeholderData: (previous) => previous
  })
  const audit = useQuery({
    queryKey: ["instance", "audit", auditPage],
    queryFn: () => api.get<Paged<AuditEvent>>(`/admin/audit?page=${auditPage}&pageSize=20`),
    enabled: user?.systemRole === "INSTANCE_ADMIN",
    placeholderData: (previous) => previous
  })
  const statusMutation = useMutation({
    mutationFn: ({ action, reason }: { action: StatusAction; reason: string }) =>
      api.patch(`/admin/${action.kind === "user" ? "users" : "families"}/${action.id}/status`, {
        status: action.nextStatus,
        reason
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["instance", "overview"] }),
        queryClient.invalidateQueries({ queryKey: ["instance", "families"] }),
        queryClient.invalidateQueries({ queryKey: ["instance", "users"] }),
        queryClient.invalidateQueries({ queryKey: ["instance", "audit"] })
      ])
      showToast("Статус обновлён")
      setStatusAction(null)
    }
  })
  const retryJobs = useMutation({
    mutationFn: () => api.post<{ retried: number }>("/admin/jobs/retry-failed"),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["instance", "overview"] }),
        queryClient.invalidateQueries({ queryKey: ["instance", "audit"] })
      ])
      showToast(`Возвращено в очередь: ${result.retried}`)
    },
    onError: (error) => showToast(toApiMessage(error, "Не удалось повторить фоновые задачи"), "danger")
  })
  if (user?.systemRole !== "INSTANCE_ADMIN")
    return (
      <div className="page">
        <ErrorState title="Системный раздел закрыт" message="Он доступен только администратору инстанса." />
      </div>
    )
  if (
    overview.isError &&
    overview.error &&
    "status" in overview.error &&
    (overview.error as { status?: number }).status === 403
  )
    return (
      <div className="page">
        <ErrorState title="Недостаточно прав" message="Войдите под аккаунтом системного администратора." />
      </div>
    )
  const stats = overview.data
  const familyPages = Math.max(1, Math.ceil((families.data?.total ?? 0) / 20))
  const userPages = Math.max(1, Math.ceil((users.data?.total ?? 0) / 20))
  const auditPages = Math.max(1, Math.ceil((audit.data?.total ?? 0) / 20))
  return (
    <div className="page page--instance">
      <PageHeader
        title="Состояние системы"
        description={
          registrationEnabled ? "Открытая регистрация включена." : "Новые семьи создаются только по приглашению."
        }
        actions={
          <>
            <Button variant="secondary" icon="plus" onClick={() => setRegistrationInviteOpen(true)}>
              Пригласить семью
            </Button>
            {stats?.outbox?.failed ? (
              <Button variant="danger" loading={retryJobs.isPending} onClick={() => retryJobs.mutate()}>
                Повторить {stats.outbox.failed} ошибок
              </Button>
            ) : stats ? (
              <Badge tone="success">система стабильна</Badge>
            ) : null}
          </>
        }
      />
      {overview.isLoading ? (
        <LoadingState rows={3} />
      ) : overview.isError ? (
        <ErrorState message={toApiMessage(overview.error)} retry={() => void overview.refetch()} />
      ) : (
        <div className="instance-stats">
          <article>
            <span className="instance-stat__icon is-violet">
              <Icon name="users" />
            </span>
            <div>
              <strong>{stats?.families ?? 0}</strong>
              <small>семей</small>
            </div>
          </article>
          <article>
            <span className="instance-stat__icon is-coral">
              <Icon name="users" />
            </span>
            <div>
              <strong>{stats?.users ?? 0}</strong>
              <small>пользователей</small>
            </div>
          </article>
          <article>
            <span className="instance-stat__icon is-gold">
              <Icon name="clock" />
            </span>
            <div>
              <strong>{stats?.outbox?.pending ?? 0}</strong>
              <small>событий в outbox</small>
            </div>
          </article>
          <article>
            <span className="instance-stat__icon is-green">
              <Icon name="wifi" />
            </span>
            <div>
              <strong>
                {stats?.outboxLagSeconds ?? 0}
                <i>с</i>
              </strong>
              <small>задержка outbox</small>
            </div>
          </article>
        </div>
      )}
      {stats ? (
        <section className="health-strip">
          {(["postgres", "valkey", "worker", "api"] as const).map((service) => {
            const status = stats.health?.[service]
            const labels = { postgres: "PostgreSQL", valkey: "Valkey", worker: "Worker", api: "API" }
            return (
              <div key={service}>
                <span className={`health-dot ${status ? `is-${status}` : ""}`} />
                <strong>{labels[service]}</strong>
                <small>
                  {status === "ok"
                    ? "работает"
                    : status === "degraded"
                      ? "нестабильно"
                      : status === "down"
                        ? "недоступен"
                        : "нет данных"}
                </small>
              </div>
            )
          })}
        </section>
      ) : null}
      <div className="instance-columns">
        <section className="panel">
          <div className="panel__heading">
            <div>
              <span className="eyebrow">Пространства</span>
              <h2>Семьи</h2>
            </div>
            <Badge>{families.data?.total ?? families.data?.items.length ?? 0}</Badge>
          </div>
          {families.isLoading ? (
            <LoadingState rows={5} />
          ) : families.isError ? (
            <ErrorState message={toApiMessage(families.error)} retry={() => void families.refetch()} />
          ) : families.data?.items.length ? (
            <>
              <div className="system-table">
                <div className="system-table__head">
                  <span>Семья</span>
                  <span>Участники</span>
                  <span>Статус</span>
                </div>
                {families.data.items.map((family) => (
                  <div className="system-table__row" key={family.id}>
                    <span>
                      <strong>{family.name}</strong>
                      <small>создана {formatRelative(family.createdAt)}</small>
                    </span>
                    <span>{family.membersCount ?? family._count?.members ?? "—"}</span>
                    <span className="button-row">
                      <Badge tone={family.status === "ACTIVE" ? "success" : "warning"}>
                        {family.status === "ACTIVE" ? "активна" : "приостановлена"}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setStatusAction({
                            kind: "family",
                            id: family.id,
                            name: family.name,
                            nextStatus: family.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE"
                          })
                        }
                      >
                        {family.status === "ACTIVE" ? "Пауза" : "Включить"}
                      </Button>
                    </span>
                  </div>
                ))}
              </div>
              {familyPages > 1 ? <Pagination page={familyPage} pages={familyPages} onChange={setFamilyPage} /> : null}
            </>
          ) : (
            <EmptyState icon="🏠" title="Семей пока нет" text="Первая зарегистрированная семья появится здесь." />
          )}
        </section>
        <section className="panel">
          <div className="panel__heading">
            <div>
              <span className="eyebrow">Аккаунты</span>
              <h2>Пользователи</h2>
            </div>
            <Badge>{users.data?.total ?? users.data?.items.length ?? 0}</Badge>
          </div>
          {users.isLoading ? (
            <LoadingState rows={5} />
          ) : users.isError ? (
            <ErrorState message={toApiMessage(users.error)} retry={() => void users.refetch()} />
          ) : users.data?.items.length ? (
            <>
              <div className="system-table">
                <div className="system-table__head">
                  <span>Пользователь</span>
                  <span>Семьи</span>
                  <span>Роль</span>
                </div>
                {users.data.items.map((systemUser) => (
                  <div className="system-table__row" key={systemUser.id}>
                    <span>
                      <strong>{systemUser.displayName}</strong>
                      <small>@{systemUser.login}</small>
                    </span>
                    <span>{systemUser.familiesCount ?? systemUser._count?.memberships ?? "—"}</span>
                    <span className="button-row">
                      <Badge
                        tone={
                          systemUser.status === "SUSPENDED"
                            ? "warning"
                            : systemUser.systemRole === "INSTANCE_ADMIN"
                              ? "violet"
                              : "neutral"
                        }
                      >
                        {systemUser.status === "SUSPENDED"
                          ? "приостановлен"
                          : systemUser.systemRole === "INSTANCE_ADMIN"
                            ? "системный админ"
                            : "пользователь"}
                      </Badge>
                      {systemUser.id !== user?.id ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setStatusAction({
                              kind: "user",
                              id: systemUser.id,
                              name: systemUser.displayName,
                              nextStatus: systemUser.status === "SUSPENDED" ? "ACTIVE" : "SUSPENDED"
                            })
                          }
                        >
                          {systemUser.status === "SUSPENDED" ? "Включить" : "Пауза"}
                        </Button>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
              {userPages > 1 ? <Pagination page={userPage} pages={userPages} onChange={setUserPage} /> : null}
            </>
          ) : (
            <EmptyState icon="👤" title="Пользователей пока нет" text="Новые аккаунты появятся после регистрации." />
          )}
        </section>
      </div>
      <div className="instance-columns">
        <section className="panel">
          <div className="panel__heading">
            <div>
              <span className="eyebrow">Фоновая работа</span>
              <h2>Outbox</h2>
            </div>
            <Badge tone={stats?.outbox?.failed ? "danger" : "success"}>
              {stats?.outbox?.failed ? "нужна проверка" : "в норме"}
            </Badge>
          </div>
          <div className="job-list">
            <article>
              <span className={`job-status ${stats?.outbox?.failed ? "is-failed" : ""}`} />
              <div>
                <strong>Ожидают публикации</strong>
                <small>{stats?.outbox?.pending ?? 0} событий в очереди</small>
              </div>
            </article>
            <article>
              <span className={`job-status ${stats?.outbox?.failed ? "is-failed" : ""}`} />
              <div>
                <strong>Ошибочные события</strong>
                <small>{stats?.outbox?.failed ?? 0} требуют повторной обработки</small>
              </div>
            </article>
          </div>
        </section>
        <section className="panel">
          <div className="panel__heading">
            <div>
              <span className="eyebrow">Безопасность</span>
              <h2>Системный аудит</h2>
            </div>
          </div>
          {audit.isLoading ? (
            <LoadingState rows={4} />
          ) : audit.isError ? (
            <ErrorState message={toApiMessage(audit.error)} retry={() => void audit.refetch()} />
          ) : audit.data?.items.length ? (
            <>
              <div className="audit-list">
                {audit.data.items.map((event) => (
                  <article className="audit-row" key={event.id}>
                    <span className="audit-row__dot" />
                    <div>
                      <strong>{event.actionLabel ?? auditActionLabel(event.action)}</strong>
                      <p>
                        {event.actorName ?? event.actor?.displayName ?? "Система"} ·{" "}
                        {event.entityLabel ?? auditTargetLabel(event.targetType)}
                        {event.outcomeLabel ? ` · ${event.outcomeLabel}` : ""}
                      </p>
                      {event.reason ? <small>Причина: {event.reason}</small> : null}
                    </div>
                    <time>{formatRelative(event.createdAt)}</time>
                  </article>
                ))}
              </div>
              {auditPages > 1 ? <Pagination page={auditPage} pages={auditPages} onChange={setAuditPage} /> : null}
            </>
          ) : (
            <EmptyState icon="🛡️" title="Критичных событий нет" text="Системные действия будут видны здесь." />
          )}
        </section>
      </div>
      <div className="admin-warning">
        <Icon name="shield" />
        <div>
          <strong>Без скрытого доступа</strong>
          <p>
            Администратор инстанса видит состояние системы. Семейный контекст открывается только явно и в режиме чтения;
            impersonation не используется.
          </p>
        </div>
      </div>
      <StatusDialog
        key={statusAction ? `${statusAction.kind}:${statusAction.id}:${statusAction.nextStatus}` : "closed"}
        action={statusAction}
        pending={statusMutation.isPending}
        {...(statusMutation.isError ? { error: toApiMessage(statusMutation.error) } : {})}
        onClose={() => setStatusAction(null)}
        onConfirm={(reason) => {
          if (statusAction) statusMutation.mutate({ action: statusAction, reason })
        }}
      />
      <RegistrationInviteDialog open={registrationInviteOpen} onClose={() => setRegistrationInviteOpen(false)} />
    </div>
  )
}
